import React from 'react';
import { X, ShoppingBag, Minus, Plus, ArrowRight, Trash2 } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { formatPrice } from '../../lib/shopify';

const CartSidebar = () => {
  const { cart, cartOpen, closeCart, checkout, loading, removeItem, updateQuantity } = useCart();

  if (!cartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        data-testid="cart-overlay"
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
        onClick={closeCart}
      />

      {/* Sidebar */}
      <div
        data-testid="cart-sidebar"
        className="fixed top-0 right-0 h-full w-full max-w-md bg-[#0a0a0a] border-l border-white/10 z-50 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <ShoppingBag className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            <h2 className="font-accent text-sm uppercase tracking-[0.2em] text-white">
              Your Cart
            </h2>
          </div>
          <button
            onClick={closeCart}
            data-testid="close-cart-button"
            className="p-2 text-white/60 hover:text-white transition-colors"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent animate-spin" />
            </div>
          ) : !cart?.lines?.length ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag className="w-16 h-16 text-white/20 mb-4" strokeWidth={1} />
              <p className="text-white/60 mb-2">Your cart is empty</p>
              <p className="text-white/40 text-sm">Add some beautiful pieces to get started</p>
            </div>
          ) : (
            <div className="space-y-6">
              {cart.lines.map((item, index) => (
                <div
                  key={item.id}
                  data-testid={`cart-item-${item.id}`}
                  className="flex space-x-4 pb-6 border-b border-white/5"
                >
                  {/* Product Image */}
                  {item.image && (
                    <div className="w-20 h-20 bg-[#111] flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.productTitle}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-white text-sm font-medium truncate pr-2">
                          {item.productTitle}
                        </h3>
                        {item.variantTitle && item.variantTitle !== 'Default Title' && (
                          <p className="text-white/40 text-xs mt-1">{item.variantTitle}</p>
                        )}
                      </div>
                      {/* Delete Button */}
                      <button
                        onClick={() => removeItem(index)}
                        data-testid={`remove-item-${index}`}
                        className="p-1 text-white/40 hover:text-red-500 transition-colors"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          className="w-7 h-7 border border-white/20 flex items-center justify-center text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-white text-sm w-8 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                          className="w-7 h-7 border border-white/20 flex items-center justify-center text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-[#D4AF37] text-sm">
                        {formatPrice(item.price * item.quantity, item.currency)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart?.lines?.length > 0 && (
          <div className="p-6 border-t border-white/10 space-y-4">
            {/* Subtotal */}
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Subtotal</span>
              <span className="text-white font-medium">
                {formatPrice(cart.subtotalAmount, cart.currency)}
              </span>
            </div>

            {/* Shipping note */}
            <p className="text-white/40 text-xs text-center">
              Shipping & taxes calculated at checkout
            </p>

            {/* Checkout Button */}
            <button
              onClick={checkout}
              data-testid="checkout-button"
              className="btn-luxury w-full flex items-center justify-center space-x-2"
            >
              <span>Checkout</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartSidebar;
